import * as Redux from '@reduxjs/toolkit';

import * as Hub from '../ogm-hub';

import type { ItemRestriction, RemoveItemRestrictionsArgs } from '../ogm-hub';

import type { IAppState } from '~/appstate';
import type { RemoteData } from '../async-actions';

export interface ItemRestrictionsPageState {
	page: RemoteData<ItemRestrictionsSuccessState, void>;
	nextRemoveRequest: RemoveItemRestrictionsArgs | null;
	listId: number;
	isSaving: boolean;
	searchTerm: string;
	take: number;
	currentPage: number;
	tableIsLoading: boolean;
	selectedItemProductNumbers: string[];
}

export type ItemRestrictionForTable = ItemRestriction & {
	id: string;
};

export interface ItemRestrictionsSuccessState {
	items: ItemRestrictionForTable[];
	listName: string;
	supplierName: string;
	totalResults: number;
}

function makeInitialState(): ItemRestrictionsPageState {
	return {
		page: { type: 'initial' },
		nextRemoveRequest: null,
		listId: -1,
		isSaving: false,
		searchTerm: '',
		take: 40,
		tableIsLoading: false,
		currentPage: 0,
		selectedItemProductNumbers: [],
	};
}

export const slice = Redux.createSlice({
	name: 'itemRestrictions',
	initialState: makeInitialState(),
	reducers: {
		itemRestrictionsPageLoadedPending(state, action) {
			state.page = { type: 'loading' };
			state.listId = action.payload.listId;
			state.searchTerm = action.payload.searchTerm;
			state.take = action.payload.take;
			state.currentPage = action.payload.currentPage;
		},
		itemRestrictionsPageFailure(state) {
			state.page.type = 'failure';
		},
		itemRestrictionsPageLoadedSuccess(state, action) {
			const items = action.payload.items.map((x: ItemRestriction) => {
				return {
					...x,
					id: x.productNumber,
				};
			});
			const { listName, supplierName, totalResults } = action.payload;
			state.page = {
				type: 'success',
				data: { items, listName, supplierName, totalResults },
			};
		},
		itemRestrictionsPageChanged(state, action) {
			const newPage = action.payload;
			state.currentPage = newPage;
		},
		itemRestrictionsUpdateSearchTerm(state, action) {
			state.searchTerm = action.payload;
		},
		itemRestrictionsSearchTermChanged(state) {
			state.currentPage = 0;
		},
		itemCheckedSuccess(state, action) {
			if (state.page.type === 'success') {
				const { isChecked, productNumber } = action.payload;
				if (
					isChecked &&
					!state.selectedItemProductNumbers.includes(productNumber)
				) {
					state.selectedItemProductNumbers.push(productNumber);
				} else if (
					!isChecked &&
					state.selectedItemProductNumbers.includes(productNumber)
				) {
					state.selectedItemProductNumbers.splice(
						state.selectedItemProductNumbers.indexOf(productNumber),
						1
					);
				}
			}
		},
		selectedDeletedPending(state) {
			if (state.page.type === 'success') {
				const nextRemoveRequest: RemoveItemRestrictionsArgs = {
					productNumbers: state.selectedItemProductNumbers,
					restrictionListId: state.listId,
				};

				state.nextRemoveRequest = nextRemoveRequest;
				state.isSaving = true;
			}
		},
		itemDeletedPending(state, action) {
			if (state.page.type === 'success') {
				const toDelete = [action.payload];

				const nextRemoveRequest: RemoveItemRestrictionsArgs = {
					productNumbers: toDelete,
					restrictionListId: state.listId,
				};

				state.nextRemoveRequest = nextRemoveRequest;
				state.isSaving = true;
			}
		},
		itemDeletedSuccess(state, action) {
			if (state.page.type === 'success') {
				state.isSaving = false;

				for (const prodNum of action.payload) {
					if (state.selectedItemProductNumbers.includes(prodNum)) {
						state.selectedItemProductNumbers.splice(
							state.selectedItemProductNumbers.indexOf(prodNum),
							1
						);
					}
				}
			}
		},
		itemRestrictionsTableLoadedPending(state) {
			state.tableIsLoading = true;
		},
		itemRestrictionsTableLoadedSuccess(state, action) {
			const mappedItems = action.payload.items.map((x: ItemRestriction) => {
				return {
					...x,
					id: x.productNumber,
				};
			});

			const { totalResults } = action.payload;
			state.page.type = 'success';
			if (state.page.type === 'success') {
				state.page.data = {
					...state.page.data,
					items: mappedItems,
					totalResults,
				};
			}

			state.tableIsLoading = false;

			if (mappedItems.length === 0 && state.currentPage !== 0) {
				state.currentPage = Math.max(
					0,
					Math.ceil(totalResults / state.take) - 1
				);
			}
		},
		listIdSet(state, action) {
			state.listId = action.payload;
		},
		resetState(state) {
			const initialState = makeInitialState();
			state.page = initialState.page;
			state.nextRemoveRequest = initialState.nextRemoveRequest;
			state.listId = initialState.listId;
			state.isSaving = initialState.isSaving;
			state.searchTerm = initialState.searchTerm;
			state.take = initialState.take;
			state.tableIsLoading = initialState.tableIsLoading;
			state.currentPage = initialState.currentPage;
			state.selectedItemProductNumbers =
				initialState.selectedItemProductNumbers;
		},
	},
});

async function fetchItemRestrictions(
	dispatch,
	listId,
	searchTerm,
	currentPage,
	take
) {
	const endcodedSearchTerm = encodeURIComponent(searchTerm);
	const items = await Hub.RestrictionsController.GetItemRestrictionPage({
		restrictionListId: listId,
		searchTerm: endcodedSearchTerm,
		skip: currentPage * take,
		take: take,
	});

	if (!items.ok || items.value.errors.length !== 0) {
		dispatch(slice.actions.itemRestrictionsPageFailure());
		return;
	}

	return items;
}

export const thunks = {
	itemRestrictionsPageLoaded(
		listId: number,
		searchTerm: string,
		currentPage: number,
		take: number
	) {
		return async (dispatch) => {
			dispatch(
				slice.actions.itemRestrictionsPageLoadedPending({
					listId,
					searchTerm,
					currentPage,
					take,
				})
			);

			const response = await fetchItemRestrictions(
				dispatch,
				listId,
				searchTerm,
				currentPage,
				take
			);
			dispatch(slice.actions.itemRestrictionsPageLoadedSuccess(response.value));
		};
	},
	pageChanged(page: number) {
		return (dispatch) => {
			dispatch(slice.actions.itemRestrictionsPageChanged(page));
		};
	},
	searchTermSet(searchTerm: string) {
		return (dispatch) => {
			dispatch(slice.actions.itemRestrictionsUpdateSearchTerm(searchTerm));
		};
	},
	searched() {
		return (dispatch) => {
			dispatch(slice.actions.itemRestrictionsSearchTermChanged());
		};
	},
	itemChecked(productNumber: string, isChecked: boolean) {
		return (dispatch) => {
			dispatch(slice.actions.itemCheckedSuccess({ productNumber, isChecked }));
		};
	},
	itemDeleted(productNumber: string) {
		return async (dispatch, getState: () => IAppState) => {
			dispatch(slice.actions.itemDeletedPending(productNumber));

			const { nextRemoveRequest } = getState().itemRestrictions;
			const response = await Hub.RestrictionsController.RemoveItemRestrictions(
				nextRemoveRequest
			);

			if (!response.ok) {
				dispatch(slice.actions.itemRestrictionsPageFailure());
				return;
			}

			dispatch(slice.actions.itemDeletedSuccess([productNumber]));

			dispatch(slice.actions.itemRestrictionsTableLoadedPending());
			const { listId, searchTerm, currentPage, take } =
				getState().itemRestrictions;
			const items = await fetchItemRestrictions(
				dispatch,
				listId,
				searchTerm,
				currentPage,
				take
			);
			dispatch(slice.actions.itemRestrictionsTableLoadedSuccess(items.value));
		};
	},
	selectedDeleted() {
		return async (dispatch, getState: () => IAppState) => {
			dispatch(slice.actions.selectedDeletedPending());

			const { nextRemoveRequest } = getState().itemRestrictions;
			const response = await Hub.RestrictionsController.RemoveItemRestrictions(
				nextRemoveRequest
			);

			if (!response.ok) {
				dispatch(slice.actions.itemRestrictionsPageFailure());
				return;
			}

			dispatch(
				slice.actions.itemDeletedSuccess(nextRemoveRequest.productNumbers)
			);

			dispatch(slice.actions.itemRestrictionsTableLoadedPending());
			const { listId, searchTerm, currentPage, take } =
				getState().itemRestrictions;
			const items = await fetchItemRestrictions(
				dispatch,
				listId,
				searchTerm,
				currentPage,
				take
			);
			dispatch(slice.actions.itemRestrictionsTableLoadedSuccess(items.value));
		};
	},
	tableLoaded() {
		return async (dispatch, getState: () => IAppState) => {
			dispatch(slice.actions.itemRestrictionsTableLoadedPending());
			const { listId, searchTerm, currentPage, take } =
				getState().itemRestrictions;
			const items = await fetchItemRestrictions(
				dispatch,
				listId,
				searchTerm,
				currentPage,
				take
			);
			dispatch(slice.actions.itemRestrictionsTableLoadedSuccess(items.value));
		};
	},
	listIdSet(listId: number) {
		return (dispatch) => {
			dispatch(slice.actions.listIdSet(listId));
		};
	},
	resetState() {
		return (dispatch) => {
			dispatch(slice.actions.resetState());
		};
	},
};
